/**
 * NavigationTabs (iter68) — canonical top-nav rendered in TWO variants
 * from a SINGLE source-of-truth `NAV_ITEMS` array in `@/design_tokens`.
 *
 * Fixes the class of bug where adding a tab in the desktop `<Tabs>` list
 * but forgetting to also add it to the separate mobile scroller (or vice
 * versa) causes tabs to silently disappear on one viewport class.
 *
 * Usage (from DashboardLayout):
 *   <NavigationTabs variant="desktop" currentValue={getCurrentTab()} onTabChange={handleTabChange} />
 *   <NavigationTabs variant="mobile"  currentValue={getCurrentTab()} onTabChange={handleTabChange} />
 *
 * Both variants read from the same `NAV_ITEMS` array, so adding, removing,
 * renaming, or reordering a tab in `design_tokens.js` propagates everywhere.
 *
 * iter248 — every tab is now a real `<a href>` so the browser's own
 * gestures work (Cmd/Ctrl+click, middle-click). A plain click is still SPA
 * navigation.
 *
 * iter249 — the desktop tabs carry a PERMANENT new-tab icon beside the label
 * (no hover reveal), sitting in reserved `pr-8` padding so it can never crowd
 * the text. With a visible button on every tab the right-click menu was dead
 * weight, so it is gone from the desktop variant. The mobile scroller keeps
 * the context menu because touch has no hover and no visible icon.
 *
 * data-testid contract:
 *   desktop → `${value}-tab`                (e.g. `feeds-tab`)
 *   mobile  → `mobile-tab-${value}`         (e.g. `mobile-tab-feeds`)
 *   new tab → `open-in-new-tab-${value}` / `context-open-in-new-tab-${value}`
 * These are guaranteed for every NAV_ITEMS entry — future test-agent runs
 * will flag the mismatch instantly if any item goes missing on one variant.
 */
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { NAV_ITEMS } from '@/design_tokens';
import {
  NavContextMenu, OpenInNewTabButton, isModifiedClick,
} from '@/components/common/NewTabAffordance';

const NavigationTabs = ({ variant, currentValue, onTabChange }) => {
  const { t } = useLanguage();
  const handle = (value) => onTabChange?.(value);

  // Plain click = SPA navigation. Modified click = hand it to the browser so
  // Cmd/Ctrl+click and shift+click behave exactly as users expect of a link.
  const handleAnchorClick = (e, value) => {
    if (isModifiedClick(e)) return;
    e.preventDefault();
    handle(value);
  };

  if (variant === 'desktop') {
    return (
      <Tabs
        value={currentValue || ''}
        onValueChange={handle}
        className="hidden md:flex flex-1 min-w-0 justify-center mx-2 lg:mx-4"
      >
        {/* The strip is content-width and centred, so nothing is ever clipped
            while there is room for it. `min-w-0` on the Tabs + `max-w-full` +
            overflow on the list is the narrow-window safety net: the strip
            scrolls instead of overrunning the logo and the header controls.
            Scrollbar hidden — it would sit on the tab labels. */}
        <TabsList className="flex max-w-full justify-start gap-0.5 overflow-x-auto bg-[#0F1730]/20 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {NAV_ITEMS.map((item) => {
            const label = t(item.labelKey);
            return (
              <div key={item.value} className="relative shrink-0">
                {/* The active-state classes MUST live on TabsTrigger, not on
                    the <a>. TabsTrigger runs them through cn()/tailwind-merge,
                    which drops shadcn's base `data-[state=active]:bg-tab-active/70`
                    in favour of the brand red. Put them on the child instead and
                    Radix's Slot concatenates both with plain clsx — no dedupe —
                    so the slate-grey base wins on stylesheet order and the
                    active tab silently stops being red.
                    `pr-8` reserves the room the new-tab icon sits in, so the
                    label can never run into it. */}
                <TabsTrigger
                  value={item.value}
                  asChild
                  className="w-full pl-2 pr-6 text-sm text-white no-underline data-[state=inactive]:hover:bg-white/10 data-[state=active]:bg-[#DC2626] data-[state=active]:text-white"
                >
                  <a
                    href={item.path}
                    type={undefined}
                    onClick={(e) => handleAnchorClick(e, item.value)}
                    data-testid={`${item.value}-tab`}
                  >
                    {label}
                  </a>
                </TabsTrigger>
                {/* iter249 — always visible (not hover-revealed) and parked in
                    the padding the label can't reach, so there is a real gap
                    between the label and the icon. Because it is always there,
                    the right-click menu is redundant and has been dropped. */}
                <OpenInNewTabButton
                  path={item.path}
                  label={label}
                  slug={item.value}
                  className="absolute right-0.5 top-1/2 -translate-y-1/2 z-10 h-5 w-5 text-white/60 hover:bg-white/20 hover:text-white"
                  iconClassName="h-3.5 w-3.5"
                />
              </div>
            );
          })}
        </TabsList>
      </Tabs>
    );
  }

  // Mobile — horizontal scroller. Anchors here too, so a long-press gets the
  // browser's native "open in new tab". No hover affordance: touch has no
  // hover, and the right-click/long-press menu covers it.
  return (
    <div
      className="md:hidden overflow-x-auto"
      style={{ backgroundColor: 'var(--color-sidebar, #0F1730)' }}
      data-testid="mobile-nav-scroller"
    >
      <div className="flex px-2 py-2 gap-1 min-w-max">
        {NAV_ITEMS.map((item) => {
          const label = t(item.labelKey);
          return (
            <NavContextMenu
              key={item.value}
              path={item.path}
              label={label}
              slug={`mobile-${item.value}`}
            >
              <a
                href={item.path}
                onClick={(e) => handleAnchorClick(e, item.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors no-underline ${
                  currentValue === item.value
                    ? 'bg-[#DC2626] text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`mobile-tab-${item.value}`}
              >
                {label}
              </a>
            </NavContextMenu>
          );
        })}
      </div>
    </div>
  );
};

export default NavigationTabs;
